import { Layers } from 'lucide-react';
import React from 'react';
import { Shape } from '../../../../types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import { useMapEditor } from '../../contexts/MapEditorContext';
import { SidebarPanel } from '../EditorLayout/SidebarPanel';
import { LayerItem } from '../layers/LayerItem';

type LayersPanelProps = {
  id: string;
  onDetach: (
    id: string,
    content: React.ReactNode,
    title: string,
    icon: any
  ) => void;
  isDetached: boolean;
};

export const LayersPanel: React.FC<LayersPanelProps> = ({
  id,
  onDetach,
  isDetached
}) => {
  const {
    shapes,
    selectedIds,
    updateShape,
    deleteShape,
    moveShape,
    setSelectedIds
  } = useMapEditor();

  const handleLayerClick = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (e.shiftKey) {
      const newSelectedIds = selectedIds.includes(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id];
      setSelectedIds(newSelectedIds);
    } else {
      setSelectedIds([id]);
    }
  };

  const handleLocate = (id: string) => {
    const shape = shapes.find((s) => s.id === id);
    if (!shape) return;

    const stage = document.querySelector(
      '.konvajs-content canvas'
    )?.parentElement;
    if (!stage) return;

    const stageWidth = stage.clientWidth;
    const stageHeight = stage.clientHeight;

    const konvaStage = (window as any).Konva.stages[0];
    if (!konvaStage) return;

    const scale = konvaStage.scaleX();
    const shapeBounds = {
      left: shape.x * scale + konvaStage.x(),
      top: shape.y * scale + konvaStage.y(),
      right: (shape.x + shape.width) * scale + konvaStage.x(),
      bottom: (shape.y + shape.height) * scale + konvaStage.y()
    };

    const isVisible =
      shapeBounds.left >= 0 &&
      shapeBounds.top >= 0 &&
      shapeBounds.right <= stageWidth &&
      shapeBounds.bottom <= stageHeight;

    if (!isVisible) {
      const shapeCenterX = shape.x + shape.width / 2;
      const shapeCenterY = shape.y + shape.height / 2;

      const newX = stageWidth / 2 - shapeCenterX * scale;
      const newY = stageHeight / 2 - shapeCenterY * scale;

      konvaStage.to({
        x: newX,
        y: newY,
        duration: 0.3,
        easing: (window as any).Konva.Easings.EaseInOut
      });
    }

    setSelectedIds([id]);
  };

  const regularShapes = shapes.filter((shape) => shape.layerType === 'shapes');
  const getSpotShapes = (shapes: Shape[]): Shape[] => {
    return shapes.flatMap((shape) => {
      if (shape.layerType === 'spots') {
        return shape;
      }
      if (shape.type === 'group' && Array.isArray(shape.children)) {
        return getSpotShapes(shape.children);
      }
      return [];
    });
  };

  const spotShapes = getSpotShapes(shapes);
  const backgroundShapes = shapes.filter(
    (shape) => shape.layerType === 'background'
  );

  const floorShapes = shapes.filter((shape) => shape.layerType === 'floor');

  return (
    <SidebarPanel
      title={'Layers'}
      icon={Layers}
      id={id}
      onDetach={onDetach}
      isDetached={isDetached}
    >
      <Tabs
        defaultValue="resources"
        className="flex h-full w-full select-none flex-col"
      >
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="resources">Resources</TabsTrigger>
          <TabsTrigger value="shapes">Shapes</TabsTrigger>
          <TabsTrigger value="floor">Floor</TabsTrigger>
          <TabsTrigger value="background">Background</TabsTrigger>
        </TabsList>
        <TabsContent value="shapes" className="flex-1 overflow-y-auto">
          {regularShapes.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              No shapes yet
            </div>
          ) : (
            regularShapes.map((shape, index) => (
              <LayerItem
                key={shape.id}
                shape={shape}
                isSelected={selectedIds.includes(shape.id)}
                onClick={(e) => handleLayerClick(shape.id, e)}
                isFirst={index === 0}
                isLast={index === regularShapes.length - 1}
              />
            ))
          )}
        </TabsContent>
        <TabsContent
          value="background"
          className="max-h-[calc(100vh-12rem)] overflow-y-auto"
        >
          {backgroundShapes.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              No background yet
            </div>
          ) : (
            backgroundShapes.map((shape, index) => (
              <LayerItem
                key={shape.id}
                shape={shape}
                isSelected={selectedIds.includes(shape.id)}
                onClick={(e) => handleLayerClick(shape.id, e)}
                isFirst={index === 0}
                isLast={index === regularShapes.length - 1}
              />
            ))
          )}
        </TabsContent>
        <TabsContent
          value="floor"
          className="max-h-[calc(100vh-12rem)] overflow-y-auto"
        >
          {floorShapes.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              No Floor yet
            </div>
          ) : (
            floorShapes.map((shape, index) => (
              <LayerItem
                key={shape.id}
                shape={shape}
                isSelected={selectedIds.includes(shape.id)}
                onClick={(e) => handleLayerClick(shape.id, e)}
                isFirst={index === 0}
                isLast={index === regularShapes.length - 1}
              />
            ))
          )}
        </TabsContent>
        <TabsContent
          value="resources"
          className="max-h-[calc(100vh-12rem)] overflow-y-auto"
        >
          {spotShapes.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              No resources yet
            </div>
          ) : (
            spotShapes.map((shape, index) => (
              <LayerItem
                key={shape.id}
                shape={shape}
                isSelected={selectedIds.includes(shape.id)}
                onClick={(e) => handleLayerClick(shape.id, e)}
                isFirst={index === 0}
                isLast={index === spotShapes.length - 1}
              />
            ))
          )}
        </TabsContent>
      </Tabs>
    </SidebarPanel>
  );
};
